/**
 * Live mode's decisions on the web, pure so `bun test` proves them.
 *
 * The version-2 wire (docs/plans/os-voice-conversation.md, step 3.8):
 *
 * - **Which wire a call runs** (`liveWireOf`): the server's `open` names the
 *   call's duplex; `half` or `full` runs the new handling, anything else (a
 *   legacy `open`, an older server) keeps today's.
 * - **What `start` says** (`liveStartContextOf`): the browser's capabilities
 *   (`platform: 'web'`, the duplex it earned (`webDuplexOf`), the echo
 *   cancelling its track read back, its VAD, the device as "<browser> on <OS>"),
 *   and the resume or the takeover when it is one.
 * - **The mic gate** on a half-duplex call (`halfDuplexHeard`, shared): every
 *   frame goes up unless the person muted, OS's voice plays, or the echo's
 *   grace after it runs. No hold, no waiting.
 * - **Reconnecting** (`reconnectDelayOf`): the next try's wait after a drop,
 *   inside the server's 15 s grace, or none once it has run out.
 * - **The look for the shipped live button** (`turnOfLook`): the shared
 *   state as the turn the button draws.
 *
 * A legacy call (os-live-mode.md, Decided 3 to 9 and 16; steps 4.1 and 4.5)
 * keeps today's handling: whose turn it is (`legacyTurnOf`) from the
 * person's Mute, the voice and the server's `mic` hold, and its gate
 * (`legacyMicHeard`).
 *
 * Both: the live button (`liveButtonOf`), and the divider's reason for an
 * ending (`liveEndReasonOf`).
 */
import {
  halfDuplexHeard,
  isLiveEndReason,
  LIVE_INPUT_RATE,
  LIVE_RECONNECT_AUDIO_MS,
  LIVE_RECONNECT_GRACE_MS,
  t,
  type AssistantLiveContext,
  type AssistantMicWhy,
  type LiveCapabilities,
  type LiveClientFrame,
  type LiveDuplexMode,
  type LiveEndReason,
  type LiveLook,
  type LiveSensitivity,
} from '@alluzo/shared';

export { halfDuplexHeard };

/** Which handling a call runs: today's (`legacy`) or the version-2 wire's. */
export type LiveWire = 'legacy' | 'v2';

/** The wire a call runs, from the server's `open`: only a half- or full-duplex call is the new one. */
export const liveWireOf = (open: { readonly duplex?: LiveDuplexMode | undefined }): LiveWire =>
  open.duplex === 'half' || open.duplex === 'full' ? 'v2' : 'legacy';

/**
 * How long a version-2 `open` may go without a `state` frame before the call
 * falls back to today's handling: an older server that named a duplex but
 * never says its state.
 */
export const LIVE_STATE_WAIT_MS = 2_000;

const BROWSERS: readonly (readonly [RegExp, string])[] = [
  [/\bEdg(?:e|A|iOS)?\//, 'Edge'],
  [/\bOPR\/|\bOpera\b/, 'Opera'],
  [/\bSamsungBrowser\//, 'Samsung Internet'],
  [/\bFirefox\/|\bFxiOS\//, 'Firefox'],
  // Headless Chrome says `HeadlessChrome/`, with no word edge before `Chrome`; it is Chrome, never the Safari its tail names (F21).
  [/\b(?:Headless)?Chrome\/|\bCriOS\/|\bChromium\//, 'Chrome'],
  [/\bSafari\//, 'Safari'],
];

const SYSTEMS: readonly (readonly [RegExp, string])[] = [
  [/\biPhone\b/, 'iPhone'],
  [/\biPad\b/, 'iPad'],
  [/\bAndroid\b/, 'Android'],
  [/\bCrOS\b/, 'ChromeOS'],
  [/\bMacintosh\b|\bMac OS X\b/, 'Mac'],
  [/\bWindows\b/, 'Windows'],
  [/\bLinux\b/, 'Linux'],
];

/**
 * The browser's name and its system, from the user agent: "Chrome on Mac".
 * Only these two names are kept, never the user agent itself. Another device
 * names this one by it ("Continue live here").
 */
export const deviceNameOf = (userAgent: string): string => {
  const browser = BROWSERS.find(([pattern]) => pattern.test(userAgent))?.[1] ?? 'Browser';
  const system = SYSTEMS.find(([pattern]) => pattern.test(userAgent))?.[1] ?? null;
  return system === null ? browser : `${browser} on ${system}`;
};

/**
 * What the browser declares in every `start`: the duplex it earned and the
 * echo cancelling its track read back (half and on until the microphone has
 * said), its VAD (step 4.4) and its attention.
 */
export const webCapabilitiesOf = (device: string, ear: { readonly duplex?: 'full' | 'half'; readonly echoCancel?: boolean } = {}): LiveCapabilities => ({
  protocol: 2,
  platform: 'web',
  duplex: ear.duplex ?? 'half',
  echoCancel: ear.echoCancel ?? true,
  vad: true,
  attention: true,
  device,
});

export type LiveStartOptions = {
  readonly device: string;
  /** Coming back to a dropped call inside its grace. */
  readonly resume?: { readonly callId: string; readonly token: string } | null;
  /** Taking over the call another device runs ("Continue live here"). */
  readonly takeover?: boolean;
  /** The duplex this browser earned (`webDuplexOf`), and whether its track cancels the echo. */
  readonly duplex?: 'full' | 'half';
  readonly echoCancel?: boolean;
  /** How readily OS takes speech as its own, from Settings › OS's voice (option B, step 6). */
  readonly sensitivity?: LiveSensitivity;
};

/** The context `start` carries: the call's own, with the capabilities, and a resume or a takeover when it is one. */
export const liveStartContextOf = (base: AssistantLiveContext, options: LiveStartOptions): AssistantLiveContext => {
  const { capabilities: _capabilities, resume: _resume, takeover: _takeover, ...rest } = base;
  const resume = options.resume ?? null;
  const takeover = resume === null && options.takeover === true;
  return {
    ...rest,
    capabilities: webCapabilitiesOf(options.device, {
      ...(options.duplex === undefined ? {} : { duplex: options.duplex }),
      ...(options.echoCancel === undefined ? {} : { echoCancel: options.echoCancel }),
    }),
    ...(resume === null ? {} : { resume }),
    ...(takeover ? { takeover: true } : {}),
    ...(options.sensitivity === undefined ? {} : { sensitivity: options.sensitivity }),
  };
};

/** The waits between reconnect tries after a drop: quick at first, then every two seconds. */
export const RECONNECT_DELAYS_MS: readonly number[] = [250, 750, 1_500, 2_000];

/** The wait before try `attempt` (0 the first), `sinceDropMs` after the drop; null once the grace is spent. */
export const reconnectDelayOf = (attempt: number, sinceDropMs: number): number | null => {
  const wait = RECONNECT_DELAYS_MS[Math.min(Math.max(0, attempt), RECONNECT_DELAYS_MS.length - 1)] ?? 2_000;
  return sinceDropMs + wait >= LIVE_RECONNECT_GRACE_MS ? null : wait;
};

/**
 * The microphone's last frames while a dropped call reconnects: at most
 * `maxMs` of them, the oldest let go first, sent ahead of anything new once
 * the call is back (the server marks them replayed, so they never confirm).
 */
export type AudioRing = {
  readonly push: (pcm: Uint8Array) => void;
  /** Every frame held, oldest first; the ring is empty after. */
  readonly drain: () => Uint8Array[];
  /** How much is held, in ms. */
  readonly heldMs: () => number;
};

/** The ms a PCM16 mono frame of `bytes` lasts at `rate`. */
export const pcmMs = (bytes: number, rate: number = LIVE_INPUT_RATE): number => (bytes / 2 / rate) * 1000;

export const createAudioRing = (maxMs: number = LIVE_RECONNECT_AUDIO_MS, rate: number = LIVE_INPUT_RATE): AudioRing => {
  let frames: Uint8Array[] = [];
  let ms = 0;
  return {
    push: (pcm) => {
      frames.push(pcm);
      ms += pcmMs(pcm.byteLength, rate);
      while (ms > maxMs && frames.length > 0) {
        const oldest = frames.shift() as Uint8Array;
        ms -= pcmMs(oldest.byteLength, rate);
      }
    },
    drain: () => {
      const held = frames;
      frames = [];
      ms = 0;
      return held;
    },
    heldMs: () => ms,
  };
};

/**
 * The person's named moves on a version-2 call, as the frames they send. The
 * I'm here tap and the orb's click are both `attend`: what comes next is for
 * OS, and the server reads it as addressed activity.
 */
export const LIVE_COMMAND_FRAMES = {
  attend: { type: 'attend' },
  imHere: { type: 'attend' },
  resumeWork: { type: 'resume_work' },
  exitQuiet: { type: 'quiet', on: false },
  carryOn: { type: 'carry_on' },
} as const satisfies Record<string, LiveClientFrame>;

export type LiveCommand = keyof typeof LIVE_COMMAND_FRAMES;

/**
 * Close codes after which a dropped version-2 call does not come back: a
 * normal close, a refusal, the server's own 1008s, and another device taking
 * the call over (`LIVE_REFUSED_CLOSE`, `LIVE_ELSEWHERE_CLOSE`).
 */
export const FINAL_CLOSE_CODES: readonly number[] = [1000, 1008, 4403, 4409];

/** Whether a socket that closed with this code, before any `closed` frame, is a drop worth coming back from. */
export const isDrop = (code: number): boolean => !FINAL_CLOSE_CODES.includes(code);

/**
 * The shared look as the turn the shipped live button draws: muted,
 * speaking and working as they are; connecting and reconnecting as
 * connecting; every other live state is listening.
 */
export const turnOfLook = (look: LiveLook): LiveTurn | 'connecting' => {
  switch (look.state) {
    case 'connecting':
    case 'reconnecting':
      return 'connecting';
    case 'muted':
      return 'muted';
    case 'speaking':
      return 'speaking';
    case 'working':
      return 'working';
    default:
      return 'listening';
  }
};

import { alluzoDuration } from '../../../../../packages/ui/src/theme/motion';

/** Where live mode is. `waiting` is the person's turn gone by: live mode muted the mic until a click. */
export type LiveTurn = 'listening' | 'working' | 'speaking' | 'waiting' | 'muted';

export type LiveTurnInput = {
  /** Muted by the person (the Mute button): it wins, and never opens by itself. */
  readonly muted: boolean;
  /** The voice's sound is still playing here. */
  readonly playing: boolean;
  /** The server's last `mic`, or null before one came. */
  readonly hold: AssistantMicWhy | null;
  /** OS has a question and has not answered it. */
  readonly working: boolean;
};

/** Whose turn it is on a legacy call. */
export const legacyTurnOf = ({ muted, playing, hold, working }: LiveTurnInput): LiveTurn => {
  if (muted) return 'muted';
  if (playing) return 'speaking';
  switch (hold) {
    case 'speaking':
      return 'speaking';
    case 'working':
      return 'working';
    case 'waiting':
      return 'waiting';
    case 'open':
      return 'listening';
    case null:
      return working ? 'working' : 'listening';
  }
};

export type MicGateInput = {
  readonly muted: boolean;
  readonly hold: AssistantMicWhy | null;
  readonly playing: boolean;
  /** `Date.now()` now, and until when the echo's grace runs. */
  readonly now: number;
  readonly quietUntil: number;
};

/** Whether a microphone frame is heard on a legacy call: sent to the server and read as the person's level. */
export const legacyMicHeard =({ muted, hold, playing, now, quietUntil }: MicGateInput): boolean =>
  !muted && (hold === null || hold === 'open') && !playing && now >= quietUntil;

/** The live button's look: which motion `.fpill` draws (`globals.css`, `[data-slot="os-live-button"]`). */
export type LiveButtonLook = 'turning' | 'listening' | 'speaking' | 'waiting' | 'muted';

export type LiveButton = {
  readonly look: LiveButtonLook;
  /** What it says, which is what a click does, and its name. */
  readonly label: string;
  /** The glyph: the mic, the struck mic (muted), or the struck speaker (Sound off while OS speaks). */
  readonly glyph: 'mic' | 'mic_off' | 'sound_off';
};

/** The live button for a call that is up, or coming up (`connecting`). */
export const liveButtonOf = (state: LiveTurn | 'connecting', sound: boolean): LiveButton => {
  switch (state) {
    case 'connecting':
      return { look: 'turning', label: t('agent.live.connecting'), glyph: 'mic' };
    case 'listening':
      return { look: 'listening', label: t('agent.voice.listening'), glyph: 'mic' };
    case 'working':
      return { look: 'turning', label: t('agent.live.talk_working'), glyph: 'mic' };
    case 'speaking':
      return sound
        ? { look: 'speaking', label: t('agent.live.talk_speaking'), glyph: 'mic' }
        : { look: 'speaking', label: t('agent.live.sound_off_talk'), glyph: 'sound_off' };
    case 'waiting':
      return { look: 'waiting', label: t('agent.live.click_to_talk'), glyph: 'mic' };
    case 'muted':
      return { look: 'muted', label: t('agent.live.muted_unmute'), glyph: 'mic_off' };
  }
};

/**
 * The line under the live button, when one says more than the button: the
 * mic still open just after OS took a question, in case something is added.
 */
export const liveHintOf = (state: LiveTurn | 'connecting', asking: boolean): string | null =>
  state === 'listening' && asking ? t('agent.live.still_listening') : null;

/**
 * Why live mode is over, once it is: Stop live, the server's own endings, a
 * server failure (`failed`), and the browser's microphone (`mic_off`
 * refused, `no_mic` missing, `mic_failed` would not start). An ending that
 * ran leaves the divider in the thread; one that never started says why as
 * an OS message there (`liveNoteOf`, `live-edges.ts`).
 */
export type LiveEnding = 'stopped' | 'idle' | 'ended' | 'quota' | 'mic_off' | 'no_mic' | 'mic_failed' | 'failed';

/**
 * The divider's reason for a call that ended by itself (the hook's
 * `onClosed`): a version-2 server names a `LiveEndReason`; a legacy server's
 * own words read as the provider's ending, and a socket that dropped with no
 * word (`socket <code>`) as the connection lost.
 */
export const liveEndReasonOf = (closed: string): LiveEndReason => {
  if (isLiveEndReason(closed)) return closed;
  return closed.startsWith('socket ') ? 'lost' : 'provider';
};

/** A running time, as the box shows it: 0:07, 1:24. */
export const clockOf = (ms: number): string => {
  const seconds = Math.max(0, Math.floor(ms / 1000));
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;
};

/**
 * How long the box takes to turn from chat to live, and back (Decided 16; the board "From chat to live mode, and
 * back"): `travel`, the same token globals.css spends on the live button's grow and shrink.
 */
export const LIVE_SWITCH_MS = alluzoDuration.travel;
